import React, { useEffect, useState } from 'react';
import { Button } from '@clearcover/atlas';
import { useAuth0 } from 'Auth';
import { Modal } from 'Shared/components/Modal';
import { patchBlockingRule } from 'Shared/services/Reznor/PatchBlockingRule';
import { extractReznorError } from 'Shared/services/Reznor/extractReznorError';
import { AttributePills } from 'BlockList/components/AttributePill';
import type { BlockingRule } from 'BlockList/types/blockingRule';
import styles from './useConfirmToggleRule.module.css';

export type UseConfirmToggleRuleOptions = {
  onSuccess: () => void;
};

export type UseConfirmToggleRuleResult = {
  openToggle: (rule: BlockingRule) => void;
  modal: React.ReactNode;
};

export const useConfirmToggleRule = (
  options: UseConfirmToggleRuleOptions
): UseConfirmToggleRuleResult => {
  const { onSuccess } = options;
  const { getToken } = useAuth0();

  const [pendingRule, setPendingRule] = useState<BlockingRule | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const openToggle = (rule: BlockingRule) => {
    setPendingRule(rule);
    setErrorMessage(null);
  };

  const cancel = () => {
    if (isSubmitting) return;
    setPendingRule(null);
    setErrorMessage(null);
  };

  // Close on Esc when the modal is open. Don't cancel mid-submit.
  useEffect(() => {
    if (pendingRule == null) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isSubmitting) cancel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingRule, isSubmitting]);

  const confirm = async () => {
    if (!pendingRule) return;
    const nextEnabled = !pendingRule.enabled;
    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const token = await getToken();
      await patchBlockingRule(token, pendingRule.id, nextEnabled);
      setPendingRule(null);
      onSuccess();
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: { status?: number; data?: unknown };
        message?: string;
      };
      const status = axiosErr.response?.status;
      const detail = extractReznorError(axiosErr.response?.data);

      if (status === 422 || status === 404) {
        setErrorMessage(detail ?? 'This rule could not be updated.');
      } else if (status === 403) {
        setErrorMessage(
          detail ?? 'You do not have permission to update this rule.'
        );
      } else {
        setErrorMessage('Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const nextEnabled = pendingRule ? !pendingRule.enabled : false;
  const title = nextEnabled ? 'Activate this rule?' : 'Deactivate this rule?';
  const bodyText = nextEnabled
    ? 'Once active, new applications will be evaluated against this rule.'
    : 'Once deactivated, new applications will no longer be evaluated against this rule.';
  const confirmVariant = nextEnabled ? 'primary' : 'danger';
  const confirmLabel = nextEnabled ? 'Activate' : 'Deactivate';

  const modal = (
    <Modal showModal={pendingRule != null} onClose={cancel}>
      <div className={styles.container}>
        <div className={styles.body}>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.message}>{bodyText}</p>
          <div className={styles.ruleBox} data-testid="toggle-rule-box">
            {pendingRule && (
              <>
                <AttributePills attributeTypes={pendingRule.attributeTypes} />
                <span className={styles.ruleName}>{pendingRule.name}</span>
              </>
            )}
          </div>
          {errorMessage && <p className={styles.error}>{errorMessage}</p>}
        </div>
        <div className={styles.footer}>
          <Button
            variant="secondary"
            onClick={cancel}
            disabled={isSubmitting}
            data-testid="cancel-button"
          >
            Cancel
          </Button>
          <Button
            variant={confirmVariant}
            onClick={confirm}
            isSubmitting={isSubmitting}
            data-testid="confirm-button"
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );

  return { openToggle, modal };
};
